import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Loader2, Globe, ArrowLeft } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LoginPage = () => {
  const { login, language, setLanguage } = useAuth();
  const { t } = useTranslation(language);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(t('loginSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1760067537639-0fb475c87657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwaW50ZXJpb3IlMjBzdW5ueXxlbnwwfHx8fDE3NzE0OTgxMjJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Modern home interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            {language === 'fr' ? 'Votre propriete entre de bonnes mains' : 'Your property in good hands'}
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            {language === 'fr' 
              ? 'Services de conciergerie professionnels pour les proprietaires absents'
              : 'Professional concierge services for property owners away from home'
            }
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col">
        {/* Home & Language switcher */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="login-home-btn">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="login-language-dropdown">
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="login-lang-en">
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="login-lang-fr">
                Francais
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="3+ Conciergerie" className="h-12 w-12 object-contain" />
              <span className="font-heading font-bold text-2xl text-foreground">Conciergerie</span>
            </Link>

            <Card className="border-0 shadow-soft">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl">{t('login')}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Entrez vos identifiants pour acceder a votre compte'
                    : 'Enter your credentials to access your account'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('password')}</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      t('login')
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">{t('noAccount')} </span>
                  <Link 
                    to="/register" 
                    className="text-primary hover:underline font-medium"
                    data-testid="register-link"
                  >
                    {t('register')}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
